import { MonetizationType, ObjectType, PresentationType } from '@/@types/graphql-types';
import { enMovieMetaDesc } from '@/components/experiments/MetaDescExp/data/translatables/en_movie_shows_meta_desc';
import { enSeasonMetaDesc } from '@/components/experiments/MetaDescExp/data/translatables/en_season_meta_desc';
import { frMovieMetaDesc } from '@/components/experiments/MetaDescExp/data/translatables/fr_movie_shows_meta_desc';
import { frSeasonMetaDesc } from '@/components/experiments/MetaDescExp/data/translatables/fr_season_meta_desc';
import { getMetaDescriptionVariants, isSupportedCountry } from '@/components/experiments/MetaDescExp/data';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { computed, ref, Ref, watch } from 'vue';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';

const EXCLUDED_PROVIDERS_SHORTNAMES = ['nfa', 'pva', 'pba', 'aim', 'itp'];
const PROVIDERS_CLEARNAMES_OVERRIDES: { [key: string]: string } = {
	ppp: 'Paramount Plus',
	itu: 'Apple TV',
};
export function useMetaDescExp(title: Ref<TitleDetail | undefined>) {
	const languageStore = useLanguageStore();
	const routingStore = useRoutingStore();

	const variantsData = ref<Record<string, string> | null>(null);
	const isLoading = ref(false);

	watch(
		[() => languageStore.country],
		async ([newCountry]) => {
			variantsData.value = null;
			if (!isSupportedCountry(newCountry)) {
				return;
			}

			isLoading.value = true;
			const objectTypeByRoute: Record<string, ObjectType> = {
				'app.detail.movies': ObjectType.Movie,
				'app.detail.show': ObjectType.Show,
				'app.detail.show.season': ObjectType.ShowSeason,
			};
			const objectType = objectTypeByRoute[routingStore.activeRoute?.name || ''] || title.value?.objectType;
			try {
				const data = await getMetaDescriptionVariants(objectType, newCountry);
				variantsData.value = data;
			} catch (error) {
				variantsData.value = null;
			} finally {
				isLoading.value = false;
			}
		},
		{ immediate: true }
	);

	const { allRegularOffers, isInternationalOffersActive } = useBuyBoxData({
		title: title as Ref<TitleDetail>,
		buyboxFilter: BuyBoxFilterOptions.ALL,
	});

	const overrideMetaDesc = computed(() => {
		if (!title.value || !variantsData.value || isInternationalOffersActive.value) {
			return null;
		}

		const currentPath = routingStore.activeRoute?.fullPath || '';
		const expTitle = variantsData.value[currentPath];
		const titleOffers = (allRegularOffers.value || [])
			.filter(
				offer =>
					!EXCLUDED_PROVIDERS_SHORTNAMES.includes(offer.package.shortName) &&
					![PresentationType.Dvd, PresentationType.Bluray].includes(offer.presentationType)
			)
			.map(offer => ({
				...offer,
				package: {
					...offer.package,
					clearName: PROVIDERS_CLEARNAMES_OVERRIDES?.[offer.package.shortName] || offer.package.clearName,
				},
			}));

		const hasNonValidOffers = titleOffers?.some(
			offer =>
				offer.monetizationType === MonetizationType.Cinema || ['jwt', 'plx'].includes(offer.package.shortName)
		);
		const atLeastOneProvider = titleOffers?.length > 0;
		if (isSupportedCountry(languageStore.country) && expTitle && !hasNonValidOffers && atLeastOneProvider) {
			const variant = expTitle.toLowerCase();
			let titleName = '';
			if (title.value.__typename === 'Season') {
				titleName = `"${title.value.show.content.title}" Season ${title.value.content.seasonNumber}`;
			} else {
				titleName = title.value.content.title;
			}
			const allOffersClearNames = [...new Set(titleOffers?.map(offer => offer.package.clearName))];
			const lastUpdatedAt = title.value.maxOfferUpdatedAt;

			const formattedDate = formatLastUpdatedDate(lastUpdatedAt, languageStore.language, languageStore.country);
			let restCount = 0;
			// Try progressively reducing provider count
			for (let providerCount = allOffersClearNames.length; providerCount >= 1; providerCount--) {
				const offersClearNames = allOffersClearNames.slice(0, providerCount);
				restCount = allOffersClearNames.length - providerCount;
				const providerNames = formatProviderNames(offersClearNames, restCount);

				const metaDescTemplate = findMetaDescTemplate(
					languageStore.country,
					titleName.length,
					providerNames.length,
					title.value.objectType,
					restCount
				);
				if (!metaDescTemplate) {
					continue;
				}

				const translatableKey = `${variant}_translatable` as keyof typeof metaDescTemplate;
				const translatable = metaDescTemplate[translatableKey] as string | undefined;

				if (!translatable) {
					continue;
				}

				const finalMetaDesc = translatable
					.replace('{{titleName}}', titleName)
					.replace('{{providerNames}}', providerNames)
					.replace('{{lastUpdatedAt}}', formattedDate)
					.replace('{{restCount}}', restCount.toString());

				const maxLengthKey = `${variant}_max_length` as keyof typeof metaDescTemplate;
				const maxLength = metaDescTemplate[maxLengthKey] as number | undefined;

				if (maxLength && finalMetaDesc.length > 158) {
					continue; // Try with fewer providers
				}
				return finalMetaDesc;
			}

			// All attempts failed
			return null;
		}
		return null;
	});

	return {
		overrideMetaDesc,
		isLoading,
	};
}

function formatProviderNames(providers: string[], restCount: number): string {
	const separator = restCount > 0 ? ', ' : ' & ';
	if (providers.length === 0) {
		return '';
	}
	if (providers.length === 1) {
		return providers[0];
	}
	if (providers.length === 2) {
		return providers.join(separator);
	}
	const lastProvider = providers[providers.length - 1];
	const restProviders = providers.slice(0, -1);
	return `${restProviders.join(', ')}${separator}${lastProvider}`.trimEnd();
}

function getTranslationsbyCountry(country: string) {
	if (country === 'FR') {
		return {
			[ObjectType.Movie]: frMovieMetaDesc,
			[ObjectType.ShowSeason]: frSeasonMetaDesc,
		};
	}
	// default to English
	return {
		[ObjectType.Movie]: enMovieMetaDesc,
		[ObjectType.ShowSeason]: enSeasonMetaDesc,
	};
}

function findMetaDescTemplate(
	country: string,
	titleLength: number,
	providerLength: number,
	objectType: ObjectType,
	restCount: number
) {
	const translationByCountry = getTranslationsbyCountry(country);
	const templates =
		objectType === ObjectType.ShowSeason
			? translationByCountry[ObjectType.ShowSeason]
			: translationByCountry[ObjectType.Movie];

	return templates.find(template => {
		const titleCondition = template.conditions.title;
		const providerCondition = template.conditions.provider;

		const titleMatches =
			(titleCondition.lt !== undefined && titleLength < titleCondition.lt) ||
			(titleCondition.gt !== undefined && titleLength > titleCondition.gt);

		const providerMatches =
			(providerCondition.lte !== undefined && providerLength <= providerCondition.lte) ||
			(providerCondition.gt !== undefined && providerLength > providerCondition.gt);

		const restCountCondition = template.conditions?.restCount || { eq: 0 };
		const restCountMatches =
			(restCountCondition?.eq !== undefined && restCount === restCountCondition.eq) ||
			(restCountCondition?.gt !== undefined && restCount > restCountCondition.gt);

		// if restCount is > 0 we skip checking providerMatches.
		return titleMatches && (restCount > 0 ? restCountMatches : providerMatches && restCountMatches);
	});
}

/**
 * Format date as "Month dd, yyyy" in the user's locale, e.g., "January 1, 2024"
 */
function formatLastUpdatedDate(dateString: string | undefined, language: string, country: string): string {
	if (!dateString) {
		return '';
	}

	const date = new Date(dateString);
	const locale = `${language}-${country}`;
	return new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', year: 'numeric' }).format(date);
}
